// Colour scale construction from palettes.json + a computed domain (logic.ts). No hard-coded
// colours here: everything comes from the palette data. Used both to build the MapLibre
// `fill-color` expression (feature-state driven) and the legend gradient/ticks.
import { scaleLinear } from "d3-scale";
import type { DivergingPalette, ScaleSpec } from "./registry";

export interface ColorStops {
  /** Data values at each stop, strictly increasing. */
  domain: number[];
  /** CSS colours, one per domain entry. */
  range: string[];
}

export function divergingStops(palette: DivergingPalette, domain: [number, number]): ColorStops {
  const [lo, hi] = domain;
  return { domain: [lo, 0, hi], range: [palette.neg, palette.mid, palette.pos] };
}

export function sequentialStops(colors: string[], domain: [number, number]): ColorStops {
  const [lo, hi] = domain;
  const n = colors.length;
  const domainVals = n === 1 ? [lo] : colors.map((_, i) => lo + ((hi - lo) * i) / (n - 1));
  return { domain: domainVals, range: colors };
}

export function stopsFor(
  scale: ScaleSpec,
  domain: [number, number],
  divergingPalette: DivergingPalette | undefined,
  sequentialPalette: string[] | undefined,
): ColorStops {
  if (scale.scale === "diverging") {
    if (!divergingPalette) throw new Error(`unknown diverging palette "${scale.palette}"`);
    return divergingStops(divergingPalette, domain);
  }
  if (!sequentialPalette) throw new Error(`unknown sequential palette "${scale.palette}"`);
  return sequentialStops(sequentialPalette, domain);
}

/** A d3 scale for sampling colours (legend gradient); domain/range come straight from stops. */
export function colorScale(stops: ColorStops): (v: number) => string {
  const scale = scaleLinear<string>().domain(stops.domain).range(stops.range).clamp(true);
  return (v: number) => scale(v);
}

/** MapLibre `fill-color` expression: grey for missing feature-state, else interpolated colour. */
export function fillColorExpression(stops: ColorStops, noDataColor = "#cccccc"): unknown[] {
  const interpolateArgs: unknown[] = ["interpolate", ["linear"], ["feature-state", "v"]];
  for (let i = 0; i < stops.domain.length; i++) {
    interpolateArgs.push(stops.domain[i], stops.range[i]);
  }
  return ["case", ["==", ["feature-state", "v"], null], noDataColor, interpolateArgs];
}

/** Evenly spaced tick values across a domain for the legend (5 ticks, per DESIGN §6). */
export function legendTicks(domain: [number, number], count = 5): number[] {
  const [lo, hi] = domain;
  if (count <= 1) return [lo];
  const step = (hi - lo) / (count - 1);
  return Array.from({ length: count }, (_, i) => lo + step * i);
}
